import React, { useEffect, useState } from 'react';
import { NavLink, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiFileText,
  FiCreditCard,
  FiBell,
  FiUser,
  FiLogOut,
  FiKey,
  FiMapPin,
  FiTool,
  FiMenu,
  FiX,
} from 'react-icons/fi';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { portalAPI } from '../../services/api';

/** Map route paths to human-readable page titles */
const PAGE_TITLES = {
  '/portal/dashboard': 'Tableau de bord',
  '/portal/invoices': 'Factures',
  '/portal/payments': 'Paiements',
  '/portal/lease-payments': 'Contrats de location',
  '/portal/announcements': 'Annonces immobilières',
  '/portal/notifications': 'Notifications',
  '/portal/profile': 'Mon Profil',
  '/portal/maintenance': 'Maintenance',
  '/portal/payment-intents': 'Mes intentions de paiement',
};

const getPageTitle = (pathname) => {
  if (PAGE_TITLES[pathname]) return PAGE_TITLES[pathname];
  for (const [key, title] of Object.entries(PAGE_TITLES)) {
    if (pathname.startsWith(key + '/')) return title;
  }
  return 'Portail Locataire';
};

const PortalLayout = () => {
  const { user, loading, logout } = usePortalAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const role = user?.role_slug || user?.role || '';
  const isPortalUser = ['client', 'locataire'].includes(role);

  // Auto-fermer le menu mobile lors d'un changement de page
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Fetch unread notification count
  useEffect(() => {
    if (!user) return;
    portalAPI.notifications
      .list({ limit: 1 })
      .then((res) => {
        const count = res.data?.unread_count ?? 0;
        setUnreadCount(count);
      })
      .catch(() => {});
  }, [user, location.pathname]);

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/portal/login" replace />;
  }

  if (!isPortalUser) {
    return <Navigate to="/dashboard" replace />;
  }

  const navItems = [
    {
      path: '/portal/dashboard',
      label: 'Tableau de bord',
      icon: FiHome,
      show: true,
    },
    {
      path: '/portal/invoices',
      label: 'Factures',
      icon: FiFileText,
      show: true,
    },
    {
      path: '/portal/payments',
      label: 'Paiements',
      icon: FiCreditCard,
      show: true,
    },
    {
      path: '/portal/lease-payments',
      label: 'Contrats',
      icon: FiKey,
      show: role === 'locataire',
    },
    {
      path: '/portal/maintenance',
      label: 'Maintenance',
      icon: FiTool,
      show: role === 'locataire',
    },
    {
      path: '/portal/announcements',
      label: 'Annonces',
      icon: FiMapPin,
      show: role === 'client',
    },
    {
      path: '/portal/notifications',
      label: 'Notifications',
      icon: FiBell,
      show: true,
      badge: unreadCount > 0 ? unreadCount : null,
    },
    {
      path: '/portal/profile',
      label: 'Profil',
      icon: FiUser,
      show: true,
    },
  ].filter((item) => item.show);

  const handleLogout = () => {
    logout();
    navigate('/portal/login', { replace: true });
  };

  const pageTitle = getPageTitle(location.pathname);

  return (
    <div className="app-layout portal-layout">
      {/* Backdrop sombre pour le mobile */}
      <div 
        className={`sidebar-backdrop ${mobileMenuOpen ? 'show' : ''}`}
        onClick={() => setMobileMenuOpen(false)}
        aria-hidden="true"
      />

      <aside className={`sidebar portal-sidebar ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="sidebar-logo">
          <div className="sidebar-logo-brand">
            <FiHome size={22} style={{ color: 'var(--primary-light, #93c5fd)' }} />
            <span>Portail Client</span>
          </div>
          <button 
            type="button"
            className="sidebar-mobile-close"
            onClick={() => setMobileMenuOpen(false)}
            aria-label="Fermer le menu"
          >
            <FiX size={18} />
          </button>
        </div>

        <nav className="sidebar-nav">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              end={item.end}
              onClick={() => setMobileMenuOpen(false)}
              className={({ isActive }) => `sidebar-link ${isActive ? 'active' : ''}`}
            >
              <span className="icon"><item.icon /></span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.badge != null && (
                <span
                  className="badge badge-info"
                  style={{ fontSize: 11, padding: '2px 7px', minWidth: 20, textAlign: 'center' }}
                >
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="sidebar-user">
          <div className="avatar">
            {user?.first_name?.[0] || 'U'}
            {user?.last_name?.[0] || ''}
          </div>
          <div className="info">
            <div className="name">{user?.first_name} {user?.last_name}</div>
            <div className="role">{user?.role_name || user?.role}</div>
          </div>
          <button className="topbar-btn" onClick={handleLogout} title="Déconnexion" style={{ color: 'rgba(255,255,255,0.8)' }}>
            <FiLogOut />
          </button>
        </div>
      </aside>

      <div className="main-content">
        <header className="topbar">
          <div className="topbar-left">
            <button 
              type="button"
              className="topbar-menu-btn" 
              onClick={() => setMobileMenuOpen((prev) => !prev)}
              aria-label="Menu"
              title="Menu"
            >
              <FiMenu size={22} />
            </button>
            <div className="topbar-page-title" style={{ fontWeight: 600, fontSize: 16, color: 'var(--text-primary)' }}>
              {pageTitle}
            </div>
          </div>

          <div className="topbar-actions">
            <button
              type="button"
              className="topbar-btn"
              onClick={() => navigate('/portal/notifications')}
              style={{ position: 'relative' }}
              title="Notifications"
            >
              <FiBell />
              {unreadCount > 0 && (
                <span className="badge-count">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>
            <button
              type="button"
              className="topbar-btn"
              onClick={() => navigate('/portal/profile')}
              title="Mon Profil"
            >
              <FiUser />
            </button>
          </div>
        </header>

        <div className="page-content">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default PortalLayout;
